import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock, Unlock, AlertCircle, CheckCircle, Trash2, X, Loader } from 'lucide-react';
import { documentApi } from '../services/api';
import { Document } from '../types';
import { formatDateTime } from '../utils/dateFormat';

type RowOutcome = 'pending' | 'unlocked' | 'skipped' | 'removed';

interface RowState {
  password: string;
  busy: boolean;
  error: string | null;
  outcome: RowOutcome;
  unsupported: boolean;
  confirmingRemove: boolean;
}

interface Props {
  open: boolean;
  caseId: string;
  lockedDocuments: Document[];
  onClose: () => void;
  onContinue: (skippedFilenames: string[]) => void;
  onDocumentsChanged: () => void;
}

const emptyRow: RowState = {
  password: '',
  busy: false,
  error: null,
  outcome: 'pending',
  unsupported: false,
  confirmingRemove: false,
};

// Formats we can decrypt server-side. Anything else (e.g. .odt) is flagged as
// "skip or remove only" in the dialog since the user can't unlock it here.
const UNLOCKABLE_EXTENSIONS = ['.pdf', '.docx', '.doc'];

function canUnlockFormat(filename: string): boolean {
  const lower = (filename ?? '').toLowerCase();
  return UNLOCKABLE_EXTENSIONS.some(ext => lower.endsWith(ext));
}

export const PasswordProtectedDialog: React.FC<Props> = ({
  open,
  caseId: _caseId,
  lockedDocuments,
  onClose,
  onContinue,
  onDocumentsChanged,
}) => {
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  // Snapshot the locked-document list on open so that resolving a row (which
  // removes it from the parent's `lockedDocuments` after refetch) doesn't cause
  // it to vanish from the dialog mid-session.
  const [snapshot, setSnapshot] = useState<Document[]>([]);

  useEffect(() => {
    if (!open) {
      setRows({});
      setSnapshot([]);
      return;
    }
    setSnapshot(lockedDocuments);
    setRows(() => {
      const next: Record<string, RowState> = {};
      for (const doc of lockedDocuments) {
        next[doc.documentId] = { ...emptyRow };
      }
      return next;
    });
    // Intentionally snapshot once per open — not on every prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRow = useCallback((id: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [id]: { ...(prev[id] ?? emptyRow), ...patch } }));
  }, []);

  const handleUnlock = useCallback(async (doc: Document) => {
    const id = doc.documentId;
    const row = rows[id];
    if (!row || !row.password) {
      updateRow(id, { error: 'Enter the password provided with the file.' });
      return;
    }
    updateRow(id, { busy: true, error: null });
    const outcome = await documentApi.unlockDocument(id, row.password);
    if (outcome === 'success') {
      // Wipe the password from local state immediately.
      updateRow(id, { password: '', busy: false, error: null, outcome: 'unlocked', unsupported: false });
      onDocumentsChanged();
    } else if (outcome === 'wrong_password') {
      updateRow(id, { busy: false, error: 'That password did not unlock the file. Try again.' });
    } else if (outcome === 'unsupported_encryption') {
      updateRow(id, {
        busy: false,
        error: 'This file uses an encryption method we cannot open. Skip or remove it to continue.',
        unsupported: true,
      });
    } else if (outcome === 'not_found') {
      updateRow(id, { busy: false, error: 'File no longer found on the server.' });
    } else {
      updateRow(id, { busy: false, error: 'Unlock failed. Please try again.' });
    }
  }, [rows, updateRow, onDocumentsChanged]);

  const handleSkip = useCallback(async (doc: Document) => {
    const id = doc.documentId;
    updateRow(id, { busy: true, error: null });
    try {
      await documentApi.skipDocument(id);
      updateRow(id, { password: '', busy: false, outcome: 'skipped' });
      onDocumentsChanged();
    } catch (e) {
      updateRow(id, { busy: false, error: 'Could not mark file as skipped.' });
    }
  }, [updateRow, onDocumentsChanged]);

  const handleRemove = useCallback(async (doc: Document) => {
    const id = doc.documentId;
    updateRow(id, { busy: true, error: null });
    try {
      await documentApi.deleteDocument(id);
      updateRow(id, { password: '', busy: false, outcome: 'removed', confirmingRemove: false });
      onDocumentsChanged();
    } catch (e) {
      updateRow(id, { busy: false, error: 'Could not remove file.', confirmingRemove: false });
    }
  }, [updateRow, onDocumentsChanged]);

  const { pending, skippedNames, allResolved } = useMemo(() => {
    let pending = 0;
    const skippedNames: string[] = [];
    for (const doc of snapshot) {
      const row = rows[doc.documentId];
      const outcome = row?.outcome ?? 'pending';
      if (outcome === 'pending') pending += 1;
      if (outcome === 'skipped') skippedNames.push(doc.filename);
    }
    return { pending, skippedNames, allResolved: snapshot.length > 0 && pending === 0 };
  }, [snapshot, rows]);

  const handleContinueClick = () => {
    if (skippedNames.length > 0) {
      setShowSkipConfirm(true);
    } else {
      onContinue(skippedNames);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-medical-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
            <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">
              Password-protected files
            </h3>
            <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
              These files cannot be analysed until they are unlocked. Enter the password
              provided with the file, exclude it from this analysis, or remove it entirely.
              Your decisions are saved as you go &mdash; close this dialog and come back
              later if you need to collect more passwords.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-medical-400 hover:text-medical-600 dark:hover:text-medical-200"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          <ul className="space-y-3">
            {snapshot.map(doc => {
              const row = rows[doc.documentId] ?? emptyRow;
              const outcome = row.outcome;
              const unlockable = canUnlockFormat(doc.filename);
              return (
                <li
                  key={doc.documentId}
                  className="border border-medical-200 dark:border-medical-700 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-medical-900 dark:text-medical-100 truncate">
                        {doc.filename}
                      </div>
                      <div className="text-xs text-medical-500 dark:text-medical-400">
                        {doc.uploadDate ? formatDateTime(doc.uploadDate) : ''}
                      </div>
                    </div>
                    {outcome === 'unlocked' && (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                        <CheckCircle className="h-4 w-4" /> Unlocked
                      </span>
                    )}
                    {outcome === 'skipped' && (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                        <AlertCircle className="h-4 w-4" /> Excluded from analysis
                      </span>
                    )}
                    {outcome === 'removed' && (
                      <span className="flex items-center gap-1 text-xs font-medium text-medical-500">
                        <Trash2 className="h-4 w-4" /> Removed
                      </span>
                    )}
                    {outcome === 'pending' && (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                        <Lock className="h-4 w-4" /> Locked
                      </span>
                    )}
                  </div>

                  {outcome === 'pending' && (
                    <div className="mt-3 space-y-2">
                      {!unlockable && (
                        <div className="text-xs text-medical-600 dark:text-medical-300 bg-medical-50 dark:bg-medical-900/40 border border-medical-200 dark:border-medical-700 rounded-md p-2">
                          We can't unlock this format in the browser. Decrypt the file on your computer and re-upload, or exclude it from this analysis.
                        </div>
                      )}
                      {unlockable && !row.unsupported && (
                        <div className="flex items-center gap-2">
                          <input
                            type="password"
                            value={row.password}
                            autoComplete="off"
                            placeholder="File password"
                            onChange={e => updateRow(doc.documentId, { password: e.target.value, error: null })}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && !row.busy && row.password) {
                                handleUnlock(doc);
                              }
                            }}
                            disabled={row.busy}
                            className="flex-1 px-3 py-1.5 text-sm border border-medical-300 dark:border-medical-600 dark:bg-medical-900 dark:text-medical-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button
                            onClick={() => handleUnlock(doc)}
                            disabled={row.busy || !row.password}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            {row.busy ? <Loader className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                            Unlock
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSkip(doc)}
                          disabled={row.busy}
                          className="text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline disabled:opacity-50"
                        >
                          Proceed without this file
                        </button>
                        <span className="text-medical-300">&middot;</span>
                        {row.confirmingRemove ? (
                          <>
                            <span className="text-xs text-medical-600 dark:text-medical-300">Remove from case?</span>
                            <button
                              onClick={() => handleRemove(doc)}
                              disabled={row.busy}
                              className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                            >
                              Yes, remove
                            </button>
                            <button
                              onClick={() => updateRow(doc.documentId, { confirmingRemove: false })}
                              disabled={row.busy}
                              className="text-xs font-medium text-medical-500 hover:underline"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => updateRow(doc.documentId, { confirmingRemove: true, error: null })}
                            disabled={row.busy}
                            className="flex items-center gap-1 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                          >
                            <Trash2 className="h-3 w-3" /> Remove file
                          </button>
                        )}
                      </div>
                      {row.error && (
                        <div className="text-xs text-red-600 dark:text-red-400">{row.error}</div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-medical-200 dark:border-medical-700">
          <div className="text-xs text-medical-500 dark:text-medical-400">
            {pending > 0
              ? `${pending} file${pending === 1 ? '' : 's'} still need${pending === 1 ? 's' : ''} a decision.`
              : 'All files resolved.'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-medical-700 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-lg"
            >
              Close
            </button>
            <button
              onClick={handleContinueClick}
              disabled={!allResolved}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                allResolved
                  ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                  : 'bg-gray-200 dark:bg-medical-700 text-gray-400 dark:text-medical-500 cursor-not-allowed'
              }`}
            >
              Continue to analysis
            </button>
          </div>
        </div>
      </div>

      {showSkipConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          onClick={() => setShowSkipConfirm(false)}
        >
          <div
            className="bg-white dark:bg-medical-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">
                  Continue without {skippedNames.length} file{skippedNames.length === 1 ? '' : 's'}?
                </h3>
                <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
                  The following file{skippedNames.length === 1 ? '' : 's'} will be excluded from the chronology:
                </p>
                <ul className="mt-2 text-sm text-medical-700 dark:text-medical-300 list-disc pl-5 max-h-40 overflow-y-auto">
                  {skippedNames.map(name => (
                    <li key={name} className="truncate">{name}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowSkipConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-medical-700 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-lg"
              >
                Back
              </button>
              <button
                onClick={() => {
                  setShowSkipConfirm(false);
                  onContinue(skippedNames);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordProtectedDialog;
